// import dependencies
import React, { Component, Fragment } from 'react';
import { observer, inject } from 'mobx-react';

import { continueFormEvent, gtm, beginCheckoutFlowEvent } from '~/common/utils/tracking';

import InputField from '~/common/components/InputField';
import Subheader from '~/common/components/Subheader';
import { EMAIL_ALREADY_USED_ERROR } from '~/common/utils/errorMessaging';
import { LMI_LOGIN_FLOW_ENABLED } from '~/common/utils/consts';

// import stylesheet
import styles from './account-info.css';

@inject('emailCaptureStore', 'modalStore', 'formsStore', 'convertToPaidStore', 'sharedFormUtilsStore', 'accountInfoStore')
@observer
export default class AccountInfo extends Component {
    state = {
        checkoutFlowAccountInfoInit: false
    };

    componentDidMount() {
        const { formsStore, convertToPaidStore } = this.props;

        // if email was saved from another form (i.e. trial signup) validate it on page entry
        if (!convertToPaidStore.isUserConvertingToPaid && formsStore.Email) this.validateEmail();
    }

    autoFillValidateEmail = (ev) => {
        const { formsStore } = this.props;
        // checks if the input field has errors and that it is not focused, therefor aoutofilled
        if (formsStore.DoesEmailHaveErrors || document.activeElement === ev.target) return;
        this.validateEmail();
    };

    validateEmail = () => {
        const { formsStore, sharedFormUtilsStore, emailCaptureStore } = this.props;

        // If there is a UserKey, then the user already signed into LMI with this email.
        // Note: If the user changes the email, then formsStore clears out the UserKey.
        // See setFormData() in /client/common/stores/ui/forms.js
        if (formsStore.UserKey) return;

        // if no data errors
        if (formsStore.doesFormHaveErrors(['Email'])) return;

        // check if email is available
        sharedFormUtilsStore.validateEmail(formsStore.LowerCaseDomainEmail, (data) => {
            // if email is already in use (already has a Grasshopper account)
            if (!data.IsAvailable) {
                return formsStore.setFieldError('Email', EMAIL_ALREADY_USED_ERROR);
            }
            // if email can authenticate in LMI Identity
            if (data.CanUserAuthenticateInLmiIdentity) {
                // clear any possible errors
                if (formsStore.doesFormHaveErrors(['Username', 'Email', 'Password', 'FirstName', 'LastName'])) {
                    formsStore.clearFieldError('Username');
                    formsStore.clearFieldError('Email');
                    formsStore.clearFieldError('Password');
                    formsStore.clearFieldError('FirstName');
                    formsStore.clearFieldError('LastName');
                }

                this.observeLoginModal();

                return undefined;
            }
            // if email is disposable email
            if (data.IsDisposableEmail) {
                return formsStore.setFieldError('Email', 'Please provide a valid email address.');
            }
            // store email
            return emailCaptureStore.addCapturedEmail(data.UserName === formsStore.Email ? formsStore.Email : data.UserName, false);
        });
    };

    observeLoginModal = () => {
        const modalFeatureType = LMI_LOGIN_FLOW_ENABLED ? 'loginLmiModal' : 'loginModal';
        if (modalFeatureType) {
            this.triggerLoginModal(modalFeatureType);
        }
    };

    triggerLoginModal = (modalFeatureType) => {
        const { modalStore } = this.props;
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        modalStore.setModalOptions({
            childComponentString: modalFeatureType,
            isModalClosable: false,
            modalType: 'xlarge',
            isModalOpen: true
        });
    };

    validateUsername = () => {
        const { formsStore, accountInfoStore } = this.props;
        // run validation
        formsStore.UsernameValidation();
        // if no data errors
        if (formsStore.doesFormHaveErrors(['Username'])) return;
        // check if username is valid
        accountInfoStore.validateUsername(formsStore.Username, () => {
            // if user is already in use set error
            if (accountInfoStore.UsernameAlreadyInUse) formsStore.setFieldError('Username', 'Username already taken. Please enter a new username.');
        }, (err) => {
            const errorData = err.response.data;
            // if username doesn't meet requirements set validation flag
            if (typeof errorData === 'object' && errorData.ErrorType === 'ModelValidationError') formsStore.setFieldError('Username', 'please enter a valid username.');

            // otherwise let the user continue as normal
            accountInfoStore.setUsernameAlreadyInUse(false);
        });
    };

    clearUsernameWarning = () => this.props.formsStore.removeFieldWarning('Username');

    // Checks if the first interaction of the form is made in this section.
    // Calls BeginCheckoutFlow event with step value.
    callBuyFormEvent() {
        const { formsStore } = this.props;
        if (!formsStore.isFormInteracted()) {
            formsStore.setFormInteracted(true);
        }

        if (!this.state.checkoutFlowAccountInfoInit) {
            this.setState({ checkoutFlowAccountInfoInit: true });
            gtm(beginCheckoutFlowEvent(2));
        }
    }

    // Once user finishes the section, call ContinueFormEvent
    blurBuyFormEvent() {
        const {
            FirstName,
            LastName,
            Email,
            Password
        } = this.props.formsStore;

        if (!!FirstName && !!LastName && !!Email && !!Password) {
            gtm(continueFormEvent('buy-form', 1));
        }
    }

    render() {
        const { formsStore, convertToPaidStore, sharedFormUtilsStore, isCheckoutABTest } = this.props;

        const {
            FirstName,
            FirstNameError,
            LastName,
            LastNameError,
            Email,
            EmailError,
            Password,
            PasswordError,
            Username,
            UsernameError,
            UsernameWarning,
            UserKey
        } = formsStore;

        const { isUserConvertingToPaid } = convertToPaidStore;
        const { isLoading } = sharedFormUtilsStore;
        const isUserKey = !!UserKey;

        return (
            <Fragment>
                <section className={styles.wrapper}>
                    <Subheader modifier={styles.header}>{isCheckoutABTest ? 'Create an Account' : 'Account Info'}</Subheader>
                    <InputField
                        allowedCharacters="@.!#$%&'*+-/=?^_`{|}~"
                        autoComplete="email"
                        dataTest="email-input"
                        defaultValue={Email}
                        disabled={isUserConvertingToPaid || isUserKey || isLoading}
                        error={EmailError}
                        id="email"
                        label="Email"
                        modifier={styles.input}
                        name="Email"
                        onBlur={this.validateEmail}
                        onChange={this.autoFillValidateEmail}
                        onFocus={() => this.callBuyFormEvent()}
                        readOnly={isUserConvertingToPaid || isUserKey || isLoading}
                        restrictCharacters="alphaNumericOnly"
                        toolTip="This email address is the main contact for your account. It will be used to communicate with you regarding billing, system updates, announcements, and other account-related matters"
                        type="email"
                    />
                    { !isUserConvertingToPaid && !isUserKey ?
                        <InputField
                            allowedCharacters="-!@#$%^&*"
                            autoComplete="off"
                            defaultValue={Password}
                            disabled={isUserConvertingToPaid || isLoading}
                            displayHintOnError={true}
                            error={PasswordError}
                            hint={
                                <Fragment>
                                    <p>Use between 8 and 29 characters including at least:</p>
                                    <p>1 uppercase letter</p>
                                    <p>1 number</p>
                                    <p>1 valid symbol, such as: ! @ # $ - % ^ & *</p>
                                    <p>No spaces</p>
                                </Fragment>
                            }
                            id="password"
                            label="Password"
                            maxLength={29}
                            modifier={styles.input}
                            name="Password"
                            onFocus={() => this.callBuyFormEvent()}
                            readOnly={isUserConvertingToPaid || isLoading}
                            restrictCharacters="alphaNumericOnly"
                            type="password"
                        />
                        : <div></div>
                    }
                    <InputField
                        allowedCharacters=" -'"
                        autoComplete="given-name"
                        dataTest="first-name-input"
                        defaultValue={FirstName}
                        disabled={isUserConvertingToPaid || isUserKey || isLoading}
                        error={FirstNameError}
                        id="first-name"
                        label="First Name"
                        maxLength={50}
                        modifier={styles.input}
                        name="FirstName"
                        onFocus={() => this.callBuyFormEvent()}
                        readOnly={isUserConvertingToPaid || isUserKey || isLoading}
                        restrictCharacters="alphaNumericOnly"
                    />
                    <InputField
                        allowedCharacters=" -'"
                        autoComplete="family-name"
                        dataTest="last-name-input"
                        defaultValue={LastName}
                        disabled={isUserConvertingToPaid || isUserKey || isLoading}
                        error={LastNameError}
                        id="last-name"
                        label="Last Name"
                        maxLength={50}
                        modifier={styles.input}
                        name="LastName"
                        onFocus={() => this.callBuyFormEvent()}
                        readOnly={isUserConvertingToPaid || isUserKey || isLoading}
                        restrictCharacters="alphaNumericOnly"
                    />
                    {
                        // Username
                        // only display if the email is already in use and the user is in directBuy flow
                    }
                    {sharedFormUtilsStore.emailAlreadyUsedError &&
                        !isUserConvertingToPaid &&
                        <InputField
                            allowedCharacters="@.!#$%&'*+-/=?^_`{|}~"
                            dataTest="username-input"
                            defaultValue={Username}
                            error={UsernameError}
                            id="username"
                            label="Username"
                            maxLength={50}
                            modifier={styles.input}
                            name="Username"
                            onBlur={this.validateUsername}
                            onFocus={() => {
                                this.clearUsernameWarning();
                                this.callBuyFormEvent();
                            }}
                            restrictCharacters="alphaNumericOnly"
                            type="text"
                            warning={UsernameWarning}
                            wrapperClass={styles.username}
                        />
                    }
                </section>
            </Fragment>
        );
    }
}
